import { RETURN_TO_QUERY_PARAM } from "@/lib/equipoNavigation";

export const TAX_SUMMARY_RETURN_LABEL_PARAM = "returnLabel";
export const TRACKING_CLIENT_PARAM = "trackingClient";
export const TRACKING_PERIOD_PARAM = "trackingPeriod";
export const TRACKING_OPEN_MONTH_PARAM = "openMonth";
export const TAREAS_FISCALES_VIEW_PARAM = "view";

const TAREAS_FISCALES_PATH = "/despacho/tareas";
const DEFAULT_RETURN_PATH = `${TAREAS_FISCALES_PATH}?tab=fiscales&view=seguimiento_anual`;

const ALLOWED_RETURN_PREFIXES = [
  "/despacho/",
  "/",
];

/**
 * @param {string} path
 */
export function isValidTaxSummaryReturnPath(path) {
  const raw = String(path || "").trim();
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("..")) return false;
  if (raw === "/") return true;
  return ALLOWED_RETURN_PREFIXES.some((prefix) => prefix !== "/" && raw.startsWith(prefix));
}

/**
 * @param {string} [search]
 */
export function parseTaxSummaryReturnToFromSearch(search = "") {
  try {
    const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    const raw = String(params.get(RETURN_TO_QUERY_PARAM) || "").trim();
    return isValidTaxSummaryReturnPath(raw) ? raw : "";
  } catch {
    return "";
  }
}

/**
 * @param {string} [search]
 */
export function parseTaxSummaryReturnLabelFromSearch(search = "") {
  try {
    const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    return String(params.get(TAX_SUMMARY_RETURN_LABEL_PARAM) || "").trim();
  } catch {
    return "";
  }
}

/**
 * URL de retorno al diálogo de captura mensual (seguimiento anual).
 * @param {{ fiscalClientId?: string, periodKey?: string }} [opts]
 */
export function buildTrackingMonthReturnPath({ fiscalClientId, periodKey } = {}) {
  const params = new URLSearchParams();
  params.set("tab", "fiscales");
  params.set(TAREAS_FISCALES_VIEW_PARAM, "seguimiento_anual");
  const client = String(fiscalClientId || "").trim();
  const period = String(periodKey || "").trim();
  if (client) params.set(TRACKING_CLIENT_PARAM, client);
  if (period) params.set(TRACKING_PERIOD_PARAM, period);
  params.set(TRACKING_OPEN_MONTH_PARAM, "1");
  return `${TAREAS_FISCALES_PATH}?${params.toString()}`;
}

/**
 * Extrae cliente/periodo del deep link embebido en returnTo.
 * @param {string} returnTo
 */
export function parseTrackingReturnContext(returnTo) {
  const raw = String(returnTo || "").trim();
  if (!raw) return null;
  try {
    const qIndex = raw.indexOf("?");
    if (qIndex < 0) return null;
    const params = new URLSearchParams(raw.slice(qIndex + 1));
    const clientId = String(params.get(TRACKING_CLIENT_PARAM) || "").trim();
    const periodKey = String(params.get(TRACKING_PERIOD_PARAM) || "").trim();
    const openMonth = params.get(TRACKING_OPEN_MONTH_PARAM) === "1";
    if (!clientId && !periodKey && !openMonth) return null;
    return { clientId, periodKey, openMonth };
  } catch {
    return null;
  }
}

/**
 * @param {string} returnTo
 */
export function isTrackingMonthReturnPath(returnTo) {
  const raw = String(returnTo || "");
  return raw.includes(TRACKING_OPEN_MONTH_PARAM)
    || raw.includes(TRACKING_PERIOD_PARAM)
    || raw.includes("seguimiento_anual");
}

/**
 * Periodo YYYY-MM por defecto al volver al seguimiento mensual.
 * @param {number} [year]
 */
export function defaultTrackingPeriodKeyForYear(year) {
  const y = Number(year);
  if (!Number.isFinite(y) || y < 2000) return "";
  const now = new Date();
  if (now.getFullYear() === y) {
    return `${y}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }
  return `${y}-01`;
}

/**
 * Destino al cerrar Resumen Impuestos: conserva el mes de trabajo y el cliente activo.
 * @param {{ returnTo?: string, activeClientId?: string, year?: number }} [opts]
 */
export function resolveTaxSummaryNavigateTarget({
  returnTo = "",
  activeClientId = "",
  year,
} = {}) {
  const safeReturn = isValidTaxSummaryReturnPath(returnTo)
    ? String(returnTo).trim()
    : DEFAULT_RETURN_PATH;
  const clientId = String(activeClientId || "").trim();
  const trackingCtx = parseTrackingReturnContext(safeReturn);
  const trackingReturn = isTrackingMonthReturnPath(safeReturn);

  if (trackingReturn || (clientId && safeReturn.startsWith(TAREAS_FISCALES_PATH))) {
    const periodKey = trackingCtx?.periodKey || defaultTrackingPeriodKeyForYear(year);
    if (clientId) {
      return buildTrackingMonthReturnPath({ fiscalClientId: clientId, periodKey });
    }
    if (trackingCtx?.clientId) {
      return buildTrackingMonthReturnPath({
        fiscalClientId: trackingCtx.clientId,
        periodKey,
      });
    }
  }

  return safeReturn;
}

/**
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {string} target
 */
export function navigateTaxSummaryReturn(navigate, target) {
  const raw = String(target || "").trim();
  if (!isValidTaxSummaryReturnPath(raw)) {
    navigate(DEFAULT_RETURN_PATH);
    return;
  }
  const qIndex = raw.indexOf("?");
  if (qIndex < 0) {
    navigate(raw);
    return;
  }
  navigate({
    pathname: raw.slice(0, qIndex),
    search: raw.slice(qIndex),
  });
}

/**
 * @param {URLSearchParams | string} searchParams
 * @param {string} [fallback]
 */
export function resolveTaxSummaryReturnTarget(searchParams, fallback = DEFAULT_RETURN_PATH) {
  const search = typeof searchParams === "string"
    ? searchParams
    : searchParams?.toString?.() || "";
  const fromQuery = parseTaxSummaryReturnToFromSearch(search ? `?${search.replace(/^\?/, "")}` : "");
  if (fromQuery) return fromQuery;
  const safeFallback = isValidTaxSummaryReturnPath(fallback) ? fallback : DEFAULT_RETURN_PATH;
  return safeFallback;
}

/**
 * @param {string} returnTo
 * @param {string} [returnLabel]
 */
export function resolveTaxSummaryReturnLabel(returnTo, returnLabel = "") {
  const custom = String(returnLabel || "").trim();
  if (custom) return custom;
  const path = String(returnTo || "");
  if (path.includes(TRACKING_OPEN_MONTH_PARAM) || path.includes(TRACKING_PERIOD_PARAM)) {
    return "Volver al seguimiento del mes";
  }
  if (path.startsWith(TAREAS_FISCALES_PATH)) return "Volver a tareas";
  return "Cerrar y volver";
}

/**
 * @param {URLSearchParams} params
 * @param {string} returnTo
 * @param {string} [returnLabel]
 */
export function appendTaxSummaryReturnParams(params, returnTo, returnLabel = "") {
  const back = String(returnTo || "").trim();
  if (isValidTaxSummaryReturnPath(back)) {
    params.set(RETURN_TO_QUERY_PARAM, back);
  }
  const label = String(returnLabel || "").trim();
  if (label) params.set(TAX_SUMMARY_RETURN_LABEL_PARAM, label);
}
